import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
type Bilingual = {
  en: string;
  fr: string;
};
const SYSTEMS: Bilingual[] = [{
  fr: 'Amilia',
  en: 'Amilia'
}, {
  fr: 'Ludik',
  en: 'Ludik'
}, {
  fr: 'Activitek',
  en: 'Activitek'
}, {
  fr: 'Sport-Plus',
  en: 'Sport-Plus'
}, {
  fr: 'Coba',
  en: 'Coba'
}, {
  fr: 'Autre',
  en: 'Other'
}];
const PRIORITIES: Bilingual[] = [{
  fr: 'Inscriptions',
  en: 'Registrations'
}, {
  fr: 'Camps de jour',
  en: 'Day camps'
}, {
  fr: 'Réservations',
  en: 'Bookings'
}, {
  fr: 'Ressources',
  en: 'Resources'
}, {
  fr: 'Paiements',
  en: 'Payments'
}, {
  fr: 'Tout le parcours',
  en: 'The whole journey'
}];
function Chip({
  label,
  selected,
  onClick,
  role
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  role: 'radio' | 'checkbox';
}) {
  return <button type="button" role={role} aria-checked={selected} onClick={onClick} className={`flex items-center gap-2 rounded-xl border px-3.5 py-2.5 font-sans text-[13.5px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]/40 ${selected ? 'border-[#4F46E5] bg-[#F4F4FE] text-[#121212]' : 'border-[#E8EAF0] bg-white text-[#3B4252] hover:border-[#C9CCE8]'}`} data-id="element-4194" data-mp="1t7k1sde3ksgo">
      <span className={`shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center ${selected ? 'bg-[#4F46E5]' : 'border-2 border-[#C9CCD6]'}`} data-id="element-4195" data-mp="2c05tclhz1wvg">
        {selected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" data-id="element-4196" data-mp="2lu4vvllyjinx" />}
      </span>
      {label}
    </button>;
}
export function LoisirsDemoCard() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const [system, setSystem] = useState('Amilia');
  const [priorities, setPriorities] = useState<string[]>(['Registrations']);
  const togglePriority = (key: string) => setPriorities(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  return <div className="rounded-3xl border border-[#EEF0F4] bg-white p-6 md:p-7 shadow-[0_24px_60px_rgba(18,18,40,0.12)]" data-id="element-4197" data-mp="27w6rannbf6n7">
      <h3 className="font-sans text-[22px] md:text-[24px] font-semibold tracking-[-0.02em] text-[#121212]" data-id="element-4198" data-mp="23sikw8af6h3j">
        {isFR ? 'Préparons votre démonstration' : 'Let’s prepare your demo'}
      </h3>
      <p className="font-sans text-[13.5px] text-[#5B6478] leading-snug mt-1.5" data-id="element-4199" data-mp="2cu8b5askf9zq">
        {isFR ? 'Sélectionnez votre système actuel et vos priorités. Nous vous montrerons comment Loisirs ATNA le remplace.' : 'Select your current system and priorities. We’ll show you how ATNA Recreation replaces it.'}
      </p>

      <fieldset className="mt-6" data-id="element-4200" data-mp="2vkzv16a6iisd">
        <legend className="font-sans text-[15px] font-semibold text-[#121212]" data-id="element-4201" data-mp="2vm15b8jdf191">
          {isFR ? 'Quel système utilisez-vous aujourd’hui ?' : 'Which system do you use today?'}
        </legend>
        <div role="radiogroup" className="mt-3 flex flex-wrap gap-2.5" data-id="element-4202" data-mp="3k64jb4fzjt53">
          {SYSTEMS.map(s => <Chip key={s.en} role="radio" label={pick(s)} selected={system === s.en} onClick={() => setSystem(s.en)} data-id="element-4203" data-mp="3651vam5fk8rj" />)}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-4204" data-mp="2wga7mq7ekamf">
        <legend className="font-sans text-[15px] font-semibold text-[#121212]" data-id="element-4205" data-mp="2wf8u8f2wzc1j">
          {isFR ? 'Que voulez-vous simplifier ?' : 'What do you want to simplify?'}
        </legend>
        <div className="mt-3 flex flex-wrap gap-2.5" data-id="element-4206" data-mp="2mim9dqal8kzi">
          {PRIORITIES.map(p => <Chip key={p.en} role="checkbox" label={pick(p)} selected={priorities.includes(p.en)} onClick={() => togglePriority(p.en)} data-id="element-4207" data-mp="2rgxitzwz0th7" />)}
        </div>
      </fieldset>

      <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
      location: 'loisirs_replace_card',
      cta: 'see_replacement',
      current_system: system,
      priorities: priorities.join(',')
    })} className="mt-7 flex items-center justify-center gap-2 rounded-full bg-[#4F46E5] px-6 py-3.5 font-sans text-[15px] font-medium text-white hover:bg-[#4338CA] transition-colors duration-150" data-id="element-4208" data-mp="2ifpqdyh4ymv1">
        {isFR ? 'Voir comment Loisirs ATNA le remplace' : 'See how ATNA Recreation replaces it'}
        <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-4209" data-mp="1moxexb0q8t1l" />
      </Link>
    </div>;
}